// contexts/CartContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'react-hot-toast';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [cartItems, setCartItems] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [cartTotal, setCartTotal] = useState(0);

  // Load cart from localStorage on initial load
  useEffect(() => {
    if (user) {
      const savedCart = localStorage.getItem(`cart_${user.uid}`);
      if (savedCart) {
        try {
          setCartItems(JSON.parse(savedCart));
        } catch (error) {
          console.error('Error loading cart:', error);
        }
      }
    } else {
      setCartItems([]);
    }
  }, [user]);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    if (user) {
      localStorage.setItem(`cart_${user.uid}`, JSON.stringify(cartItems));
    }
    
    // Calculate cart count and total
    const count = cartItems.reduce((sum, item) => sum + item.quantity, 0);
    const total = cartItems.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);
    
    setCartCount(count);
    setCartTotal(total);
  }, [cartItems, user]);

  // Add item to cart
  const addToCart = (item) => {
    if (!user) {
      toast.error('Please login to add items to cart');
      return;
    }

    setCartItems(prevItems => {
      // Check if item already exists in cart
      const existingItemIndex = prevItems.findIndex(
        i => i.productId === item.productId && 
        (i.selectedColor || '') === (item.selectedColor || '')
      );

      if (existingItemIndex >= 0) {
        // Update quantity of existing item
        const updatedItems = [...prevItems];
        const newQuantity = updatedItems[existingItemIndex].quantity + item.quantity;
        
        // Check if quantity exceeds max available
        if (newQuantity > (item.maxQuantity || 10)) {
          toast.error(`Only ${item.maxQuantity || 10} items available`);
          return prevItems;
        }
        
        updatedItems[existingItemIndex].quantity = newQuantity;
        toast.success('Cart updated successfully!');
        return updatedItems;
      } else {
        // Add new item
        toast.success('Item added to cart!');
        return [...prevItems, item];
      }
    });
  };

  // Remove item from cart
  const removeFromCart = (productId, selectedColor = '') => {
    setCartItems(prevItems => 
      prevItems.filter(item => 
        !(item.productId === productId && (item.selectedColor || '') === selectedColor)
      )
    );
    toast.success('Item removed from cart');
  };

  // Update item quantity
  const updateQuantity = (productId, selectedColor, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(productId, selectedColor);
      return;
    }

    setCartItems(prevItems =>
      prevItems.map(item => {
        if (item.productId === productId && (item.selectedColor || '') === selectedColor) {
          // Check if quantity exceeds max available
          if (newQuantity > (item.maxQuantity || 10)) {
            toast.error(`Only ${item.maxQuantity || 10} items available`);
            return item;
          }
          return { ...item, quantity: newQuantity };
        }
        return item;
      })
    );
  };

  // Clear cart
  const clearCart = () => {
    setCartItems([]);
    toast.success('Cart cleared');
  };

  const value = {
    cartItems,
    cartCount,
    cartTotal,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};